// Pure coordinate math reconciling the three spaces a scan touches: the decoder's
// ROI-canvas pixels, the full camera frame, and the CSS-sized <video> element the
// attendee actually sees (usually object-fit: cover, which crops asymmetrically
// whenever the frame and display aspect ratios differ). No DOM access — the highest
// -risk module in the scanning pipeline gets to be the easiest one to get right in a test.
// Ported from event-connect's scanner (geometry.ts) unchanged.

import type { CornerPoints, Point, Rect, Size } from './decoder/types'

export type ObjectFit = 'cover' | 'contain'

function fitScale(frameSize: Size, displaySize: Size, fit: ObjectFit): number {
  const scaleX = displaySize.width / frameSize.width
  const scaleY = displaySize.height / frameSize.height
  return fit === 'cover' ? Math.max(scaleX, scaleY) : Math.min(scaleX, scaleY)
}

function fitOffset(frameSize: Size, displaySize: Size, scale: number): Point {
  const renderedWidth = frameSize.width * scale
  const renderedHeight = frameSize.height * scale
  return {
    x: (displaySize.width - renderedWidth) / 2,
    y: (displaySize.height - renderedHeight) / 2,
  }
}

/** Frame-space pixel → on-screen CSS pixel, under the video element's object-fit. */
export function frameToDisplay(p: Point, frameSize: Size, displaySize: Size, fit: ObjectFit): Point {
  const scale = fitScale(frameSize, displaySize, fit)
  const offset = fitOffset(frameSize, displaySize, scale)
  return { x: p.x * scale + offset.x, y: p.y * scale + offset.y }
}

/**
 * On-screen CSS pixel → frame-space pixel — the inverse frameToDisplay doesn't provide.
 * Needed to turn a viewfinder rect authored in display space into the ROI crop rect the
 * frame loop actually reads pixels from.
 */
export function displayToFrame(p: Point, frameSize: Size, displaySize: Size, fit: ObjectFit): Point {
  const scale = fitScale(frameSize, displaySize, fit)
  const offset = fitOffset(frameSize, displaySize, scale)
  return { x: (p.x - offset.x) / scale, y: (p.y - offset.y) / scale }
}

/**
 * ROI-canvas pixel → frame-space pixel. Only correct when the ROI was drawn 1:1
 * (destination width/height equal the source crop's); any downscale in the drawImage
 * call silently invalidates this translation.
 */
export function roiToFrame(p: Point, roi: Rect): Point {
  return { x: p.x + roi.x, y: p.y + roi.y }
}

/** Front camera streams are mirrored on screen; detections need the same flip to line up. */
export function mirrorIfFrontCamera(p: Point, displaySize: Size, isFront: boolean): Point {
  return isFront ? { x: displaySize.width - p.x, y: p.y } : p
}

/**
 * Reorders a decoder's four corner points into a canonical clockwise-from-visual
 * -top-left order, using only their geometry — not whichever corner the decoder calls
 * "topLeft" (that label is unreliable under rotation/mirroring).
 */
export function normalizeCorners(corners: CornerPoints): CornerPoints {
  const cx = corners.reduce((sum, p) => sum + p.x, 0) / corners.length
  const cy = corners.reduce((sum, p) => sum + p.y, 0) / corners.length

  const sorted = [...corners].sort((a, b) => Math.atan2(a.y - cy, a.x - cx) - Math.atan2(b.y - cy, b.x - cx))

  let startIndex = 0
  let bestScore = Infinity
  sorted.forEach((p, i) => {
    const score = p.x + p.y
    if (score < bestScore) {
      bestScore = score
      startIndex = i
    }
  })

  return [
    sorted[startIndex % 4],
    sorted[(startIndex + 1) % 4],
    sorted[(startIndex + 2) % 4],
    sorted[(startIndex + 3) % 4],
  ]
}

/** The axis-aligned bounding box of a quad — used by both decoder implementations and the
 * loop's display-space mapping, so it lives here rather than being reimplemented at each call site. */
export function boundingBoxOf(corners: CornerPoints): Rect {
  const xs = corners.map(p => p.x)
  const ys = corners.map(p => p.y)
  const minX = Math.min(...xs)
  const minY = Math.min(...ys)
  return { x: minX, y: minY, width: Math.max(...xs) - minX, height: Math.max(...ys) - minY }
}
